import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Drawer,
  Typography,
  useMediaQuery,
  useTheme,
  IconButton,
} from "@mui/material";
import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import LogoutIcon from "@mui/icons-material/Logout";
import LoginIcon from "@mui/icons-material/Login";
import SearchIcon from "@mui/icons-material/Search";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import { useAuth } from "../../contexts/AuthContext";
import { uiNeutrals } from "../../constants/uiNeutrals";
import { closeIconButtonSx } from "../../constants/closeIconButtonSx";
import { appBackgroundColor } from "../../theme";
import CustomButton from "../ui/CustomButton";

const UserMenu = ({ open, onClose, navLinks = [] }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user, logout } = useAuth();
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);

  const handleLogoutConfirm = () => {
    setLogoutConfirmOpen(false);
    onClose();
    logout();
    navigate("/");
  };

  const handleLoginClick = () => {
    onClose();
    navigate("/login");
  };

  return (
    <>
    <Dialog
      open={logoutConfirmOpen}
      onClose={() => setLogoutConfirmOpen(false)}
      maxWidth="xs"
      fullWidth
      sx={{ zIndex: 10001 }}
      PaperProps={{
        sx: { borderRadius: "25px" },
      }}
    >
      <DialogTitle sx={{ fontSize: "1.05rem", fontWeight: 600, pb: 0.5 }}>
        Log out?
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ color: "#555", fontSize: "0.9rem" }}>
          You will need to sign in again to access your account.
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 2, gap: 1 }}>
        <Button
          onClick={() => setLogoutConfirmOpen(false)}
          color="inherit"
          sx={{ textTransform: "none", fontWeight: 500 }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleLogoutConfirm}
          variant="text"
          disableElevation
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: 999,
            px: 2.5,
            py: 0.75,
            minWidth: 0,
            color: "#dc2626",
            bgcolor: "#fee2e2",
            "&:hover": {
              bgcolor: "#fecaca",
              color: "#b91c1c",
            },
          }}
        >
          Log out
        </Button>
      </DialogActions>
    </Dialog>

    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      sx={{ zIndex: 10000 }}
      ModalProps={{
        keepMounted: true,
      }}
      PaperProps={{
        elevation: 0,
        sx: {
          width: "min(296px, 88vw)",
          maxWidth: "100%",
          display: "flex",
          flexDirection: "column",
          bgcolor: appBackgroundColor,
          height: "100%",
          borderRight: `1px solid ${uiNeutrals.surfaceBorder}`,
          borderTopRightRadius: "20px",
          borderBottomRightRadius: "20px",
          boxShadow: "8px 0 32px rgba(0, 0, 0, 0.08)",
          pt: "env(safe-area-inset-top, 0px)",
          pb: "env(safe-area-inset-bottom, 0px)",
          pl: "env(safe-area-inset-left, 0px)",
        },
      }}
      BackdropProps={{
        sx: {
          backgroundColor: "rgba(255, 255, 255, 0.2)",
          backdropFilter: "blur(5px) saturate(1.05)",
          WebkitBackdropFilter: "blur(5px) saturate(1.05)",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
          px: 2,
          py: 1.5,
          borderBottom: `1px solid ${uiNeutrals.surfaceBorder}`,
          flexShrink: 0,
        }}
      >
        <Typography
          sx={{
            fontSize: "15px",
            fontWeight: 600,
            color: "#000",
          }}
        >
          Menu
        </Typography>

        <IconButton
          onClick={onClose}
          edge="end"
          sx={closeIconButtonSx}
          aria-label="Close menu"
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          px: 3,
          pt: 2.5,
          WebkitOverflowScrolling: "touch",
          touchAction: "pan-y",
          overscrollBehavior: "contain",
          "&::-webkit-scrollbar": {
            display: "none",
          },
          msOverflowStyle: "none",
          scrollbarWidth: "none",
        }}
      >
        {isAuthenticated ? (
          <Box sx={{ textAlign: "left", mb: 2.5 }}>
            <Typography
              sx={{
                color: "#000",
                fontSize: "1.0625rem",
                fontWeight: 600,
                lineHeight: 1.35,
              }}
            >
              {user?.firstName} {user?.lastName}
            </Typography>
            <Typography
              color="text.secondary"
              sx={{
                mt: 0.75,
                fontSize: isMobile ? "0.8125rem" : "0.875rem",
                lineHeight: 1.4,
              }}
            >
              {user?.email}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ textAlign: "left", mb: 2.5 }}>
            <Typography
              sx={{
                color: "#666",
                fontWeight: 400,
                fontSize: "0.875rem",
                lineHeight: 1.45,
              }}
            >
              Please login to access your account
            </Typography>
          </Box>
        )}

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
            position: "relative",
          }}
        >
          {navLinks.map((link) => {
            if (link.label === "Login") return null;
            const isActive =
              link.to === "/"
                ? location.pathname === "/" || location.pathname === "/home"
                : location.pathname === link.to;
            return (
              <Box key={link.to} sx={{ width: "100%" }}>
                <Box
                  onClick={() => {
                    navigate(link.to);
                    onClose();
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    gap: 1.25,
                    py: 1.75,
                    cursor: "pointer",
                  }}
                >
                  {link.label === "Home" && (
                    <ArrowUpwardIcon
                      sx={{
                        fontSize: 20,
                        color: isActive ? "#000" : "#999",
                        transform: "rotate(-45deg)",
                      }}
                    />
                  )}
                  {link.label === "Browse" && (
                    <SearchIcon
                      sx={{
                        fontSize: 20,
                        color: isActive ? "#000" : "#999",
                      }}
                    />
                  )}
                  <Typography
                    sx={{
                      flex: 1,
                      fontSize: "0.9375rem",
                      color: isActive ? "#000" : "#999",
                      fontWeight: 500,
                      textAlign: "left",
                    }}
                  >
                    {link.label}
                  </Typography>
                </Box>
              </Box>
            );
          })}

          <Box
            onClick={() => {
              navigate("/reminders");
              if (!location.pathname.startsWith("/deals")) {
                onClose();
              }
            }}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              gap: 1.25,
              py: 1.75,
              cursor: "pointer",
            }}
          >
            <NotificationsOutlinedIcon
              sx={{
                fontSize: 20,
                color: location.pathname === "/reminders" ? "#000" : "#999",
              }}
            />
            <Typography
              sx={{
                flex: 1,
                fontSize: "0.9375rem",
                color: location.pathname === "/reminders" ? "#000" : "#999",
                fontWeight: 500,
                textAlign: "left",
              }}
            >
              Reminders
            </Typography>
          </Box>

          {!isAuthenticated && (
            <Box sx={{ display: "flex", justifyContent: "flex-start", pt: 2 }}>
              <CustomButton
                variant="secondary"
                onClick={handleLoginClick}
                sx={{
                  paddingY: 0.875,
                  paddingX: 2,
                  fontSize: "0.8125rem",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                }}
              >
                <LoginIcon sx={{ fontSize: 18 }} />
                Login
              </CustomButton>
            </Box>
          )}

          {isAuthenticated && (
            <Box
              onClick={() => setLogoutConfirmOpen(true)}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                gap: 1.25,
                py: 1.75,
                mt: 0.5,
                cursor: "pointer",
                mx: -3,
                px: 3,
                "&:hover": { bgcolor: "rgba(220, 38, 38, 0.06)" },
              }}
            >
              <LogoutIcon
                sx={{
                  fontSize: 20,
                  color: "#dc2626",
                }}
              />
              <Typography
                sx={{
                  flex: 1,
                  fontSize: "0.9375rem",
                  color: "#dc2626",
                  fontWeight: 500,
                  textAlign: "left",
                }}
              >
                Logout
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Drawer>
    </>
  );
};

export default UserMenu;
